import {
  Group,
  Select,
  ThemeIcon,
  Tooltip,
  TextInput,
  ActionIcon,
} from "@mantine/core";
import { IconChecks, IconSearch, IconX } from "@tabler/icons-react";
import { TableActions, TableAction, actionsColumn } from "../TableActions";
import { IconEdit } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";

import { DataTable } from "../DataTable";
import { useGetUsers } from "../../api/user";
import RoleBadges from "../RoleBadges";
import DeleteUserModal from "./DeleteUserModal";
import SendInviteMailModal from "./SendInviteMailModal";

function UsersTable() {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({
    columnAccessor: "firstName",
    direction: "asc",
  });
  const [filters, setFilters] = useState({
    name: null,
    email: null,
    role: null,
  });

  const [debouncedFilters] = useDebouncedValue(filters, 200);

  const { data, isLoading } = useGetUsers({
    page,
    limit: 100,
    sort,
    ...debouncedFilters,
  });

  const navigate = useNavigate();

  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <DataTable
      records={data?.data}
      columns={[
        {
          accessor: "firstName",
          sortable: true,
          filter: (
            <FilterSearch
              query={filters.name}
              setQuery={onFilterChange((value) =>
                setFilters({ ...filters, name: value })
              )}
              columnName="firstName"
            />
          ),
          filtering: filters.name,
        },
        {
          accessor: "lastName",
          sortable: true,
          filter: (
            <FilterSearch
              query={filters.name}
              setQuery={onFilterChange((value) =>
                setFilters({ ...filters, name: value })
              )}
              columnName="lastName"
            />
          ),
          filtering: filters.name,
        },
        {
          accessor: "email",
          sortable: true,
          filter: (
            <FilterSearch
              query={filters.email}
              setQuery={onFilterChange((value) =>
                setFilters({ ...filters, email: value })
              )}
              columnName="email"
            />
          ),
          filtering: filters.email,
          render: (row) => (
            <Group justify="space-between">
              {row.email}
              {row.isVerified ? (
                <Tooltip label="User registered">
                  <ThemeIcon color="green" variant="subtle" size="sm">
                    <IconChecks />
                  </ThemeIcon>
                </Tooltip>
              ) : (
                <SendInviteMailModal user={row} />
              )}
            </Group>
          ),
        },
        {
          accessor: "role",
          filter: (
            <Select
              label="Filter by role"
              placeholder="All roles"
              value={filters.role}
              onChange={onFilterChange((value) =>
                setFilters({ ...filters, role: value })
              )}
              data={[
                { value: "sysAdmin", label: "System Admins" },
                {
                  value: "wsAdmin",
                  label: "Workspace Admins",
                },
                { value: "member", label: "Members" },
              ]}
              clearable
            />
          ),
          render: (row) => {
            if (row?.config?.isTicketAdmin) {
              return (
                <>
                  <RoleBadges roleName="wsAdmin" />
                  <RoleBadges roleName="ticketAdmin" />
                </>
              );
            }

            return <RoleBadges roleName={row.role} />;
          },
        },
        actionsColumn((row) => (
          <TableActions>
            <TableAction
              label="Edit"
              icon={IconEdit}
              color="blue"
              onClick={() => navigate(`/app/users/${row.id}`)}
            />
            <DeleteUserModal userId={row.id} />
          </TableActions>
        )),
      ]}
      sortStatus={sort}
      onSortStatusChange={setSort}
      page={page}
      onPageChange={setPage}
      recordsPerPage={data?.pagination.perPage}
      totalRecords={data?.pagination.total}
      fetching={isLoading}
    />
  );
}

function FilterSearch({ query, setQuery, columnName }) {
  // Transform camel case to readable text
  const camelToReadableColumnName = columnName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return (
    <TextInput
      label="Users"
      description={`Show users whose ${camelToReadableColumnName} includes the specified text`}
      placeholder="Search users..."
      leftSection={<IconSearch size={16} />}
      rightSection={
        <ActionIcon
          size="sm"
          variant="transparent"
          c="dimmed"
          onClick={() => setQuery(null)}
        >
          <IconX size={14} />
        </ActionIcon>
      }
      value={query || ""}
      onChange={(e) => {
        if (e.currentTarget.value === "") {
          setQuery(null);
          return;
        }
        setQuery(e.currentTarget.value);
      }}
    />
  );
}

export default UsersTable;
